<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { ArrowLeft, Camera, CloudOff, ImagePlus } from 'lucide-vue-next'
import type { ChecklistStatus, MediaAssetSummary } from '@ck/domain'
import { sortStagesBySequence } from '@ck/domain'
import { CkAlert, CkButton, CkLightbox, CkProgress, CkSegmented, CkStatus } from '@ck/ui'
import { api, checklistsApi, mediaApi, stagesApi } from '@ck/api-client'
import { checklistDb, enqueueOperation, type PendingOperation } from '../offline'
import { useWorkOrderMedia } from '../composables/useWorkOrderMedia'
import { useWorkOrderCover } from '../composables/useWorkOrderCover'
import { compressImage } from '../lib/compressImage'
import StageRepairs from '../components/StageRepairs.vue'

interface Item { id: string; title: string; group: string; status: ChecklistStatus; note: string; version: number }

const props = defineProps<{ id: string; stageId: string }>()
const router = useRouter()
const queryClient = useQueryClient()

const pending = ref<PendingOperation[]>([])
const online = ref(typeof navigator === 'undefined' ? true : navigator.onLine)
const syncing = ref(false)
const completing = ref(false)
const completeError = ref(false)

const checklistQuery = useQuery({ queryKey: ['checklists', props.id], queryFn: () => checklistsApi.get(props.id) })
const sortedStages = computed(() => sortStagesBySequence(checklistQuery.data.value ?? []))
const stage = computed(() => sortedStages.value.find((candidate) => candidate.id === props.stageId))
const stageIndex = computed(() => stage.value ? sortedStages.value.findIndex((candidate) => candidate.id === stage.value?.id) : -1)
const previousStage = computed(() => stageIndex.value > 0 ? sortedStages.value[stageIndex.value - 1] : undefined)
const nextStage = computed(() => stageIndex.value >= 0 && stageIndex.value < sortedStages.value.length - 1 ? sortedStages.value[stageIndex.value + 1] : undefined)

const workOrderId = computed(() => props.id)
const { mediaQuery, photoUrls, remove: removeMedia, removingId } = useWorkOrderMedia(workOrderId)
const isArrival = computed(() => stage.value?.code === 'arrival')
const { coverUrl, uploadCover, uploading: coverUploading, uploadError: coverUploadError } = useWorkOrderCover(workOrderId)
const photosByItem = computed(() => (mediaQuery.data.value ?? []).reduce<Record<string, MediaAssetSummary[]>>((result, asset) => {
  if (!asset.checklistItemResultId) return result
  const bucket = (result[asset.checklistItemResultId] ??= [])
  bucket.push(asset)
  return result
}, {}))
const openPhotoId = ref<string | null>(null)
const openPhoto = computed(() => mediaQuery.data.value?.find((asset) => asset.id === openPhotoId.value))
async function removeOpenPhoto() {
  if (!openPhotoId.value) return
  await removeMedia(openPhotoId.value)
  openPhotoId.value = null
}

const items = ref<Item[]>([])
async function refreshPending() { pending.value = await checklistDb.operations.orderBy('createdAt').toArray() }
watch(stage, async (current) => {
  await refreshPending()
  if (!current) { items.value = []; return }
  const pendingByItem = new Map(pending.value.map((operation) => [operation.itemId, operation]))
  items.value = current.items.map((item) => {
    const operation = pendingByItem.get(item.id)
    return operation
      ? { id: item.id, title: item.title, group: item.group, status: operation.status, note: operation.note ?? '', version: item.version }
      : { id: item.id, title: item.title, group: item.group, status: item.status, note: item.note ?? '', version: item.version }
  })
}, { immediate: true })

const done = computed(() => items.value.filter((item) => item.status !== 'pending').length)
const progress = computed(() => items.value.length === 0 ? 0 : done.value / items.value.length * 100)
const groups = computed(() => items.value.reduce<Record<string, Item[]>>((result, item) => {
  (result[item.group] ??= []).push(item)
  return result
}, {}))
const stagePending = computed(() => {
  const itemIds = new Set(items.value.map((item) => item.id))
  return pending.value.filter((operation) => itemIds.has(operation.itemId))
})
const canComplete = computed(() => Boolean(stage.value) && items.value.length > 0 && done.value === items.value.length && stagePending.value.length === 0 && online.value && !completing.value)

async function setStatus(item: Item, status: ChecklistStatus) {
  item.status = status
  await enqueueOperation({ itemId: item.id, expectedVersion: item.version, status, note: item.note, capturedAt: new Date().toISOString() })
  await refreshPending()
  scheduleSync()
}

// Auto-sync: depois de cada avaliação, esvazia a fila sozinho (~250 ms) para que "Concluir
// etapa" habilite sem o mecânico precisar clicar "Sincronizar" a cada etapa. O botão manual
// continua como fallback offline.
let syncTimer: ReturnType<typeof setTimeout> | undefined
function scheduleSync() {
  if (syncTimer) clearTimeout(syncTimer)
  syncTimer = setTimeout(() => { void sync() }, 250)
}
async function sync() {
  if (!online.value || pending.value.length === 0) return
  // Evita sync concorrente: se um envio já está em voo, reagenda em vez de sobrepor — assim
  // operações enfileiradas durante a requisição não ficam presas.
  if (syncing.value) { scheduleSync(); return }
  syncing.value = true
  try {
    const result = await api<{ accepted: Array<{ operationId: string }>; conflicts: Array<{ operationId: string }> }>(
      '/sync/checklist-operations', { method: 'POST', body: JSON.stringify({ operations: pending.value }) }
    )
    // Um conflito de versão aqui quase sempre é o próprio mecânico corrigindo o mesmo item
    // antes de sincronizar (a primeira tentativa já aplicou e avançou a versão no servidor).
    // Reenviar a mesma operação em conflito para sempre travaria "Concluir etapa" com
    // pendência que nunca resolve — descartamos e confiamos no estado atual do servidor.
    const resolvedIds = [...result.accepted, ...result.conflicts].map((entry) => entry.operationId)
    await checklistDb.operations.bulkDelete(resolvedIds)
    await refreshPending()
    await checklistQuery.refetch()
  } finally { syncing.value = false }
}

function navigateToStage(stageId: string | undefined) {
  if (!stageId) return
  router.push({ name: 'checklist', params: { id: props.id, stageId } })
}

const fileInput = ref<HTMLInputElement | null>(null)
const uploadTargetItemId = ref<string | null>(null)
const uploadingItemId = ref<string | null>(null)
const uploadError = ref(false)

function triggerPhotoUpload(itemId: string) {
  uploadTargetItemId.value = itemId
  fileInput.value?.click()
}
async function onFileSelected(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  input.value = ''
  if (!file || !uploadTargetItemId.value) return
  uploadingItemId.value = uploadTargetItemId.value
  uploadError.value = false
  try {
    const compressed = await compressImage(file)
    await mediaApi.upload({ file: compressed, workOrderId: props.id, checklistItemId: uploadTargetItemId.value, visibleToCustomer: true, phase: 'during' })
  } catch {
    uploadError.value = true
  } finally {
    uploadingItemId.value = null
    uploadTargetItemId.value = null
  }
}

const coverInput = ref<HTMLInputElement | null>(null)
function triggerCoverUpload() { coverInput.value?.click() }
async function onCoverSelected(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  input.value = ''
  if (!file) return
  await uploadCover(file)
}

async function completeStage() {
  if (!stage.value || !canComplete.value) return
  const followingStageId = nextStage.value?.id
  completing.value = true
  completeError.value = false
  try {
    await stagesApi.complete(props.id, stage.value.id)
    // Invalida as telas que dependem das etapas para o stepper refletir a conclusão ao voltar.
    await queryClient.invalidateQueries({ queryKey: ['work-order', props.id] })
    await queryClient.invalidateQueries({ queryKey: ['checklists', props.id] })
    if (followingStageId) navigateToStage(followingStageId)
    else router.push({ name: 'veiculo', params: { id: props.id } })
  } catch {
    completeError.value = true
  } finally {
    completing.value = false
  }
}

function onOnline() { online.value = true; void sync() }
function onOffline() { online.value = false }
onMounted(() => {
  window.addEventListener('online', onOnline)
  window.addEventListener('offline', onOffline)
  void refreshPending()
})
onBeforeUnmount(() => {
  if (syncTimer) clearTimeout(syncTimer)
  window.removeEventListener('online', onOnline)
  window.removeEventListener('offline', onOffline)
})
</script>

<template>
  <div class="app">
    <header class="page-header">
      <CkButton variant="ghost" class="back" aria-label="Voltar para a OS" @click="router.push({ name: 'veiculo', params: { id } })"><ArrowLeft :size="20" aria-hidden="true" /></CkButton>
      <div><div class="ck-eyebrow">Etapa {{ stage ? String(stage.sequence).padStart(2, '0') : '' }}</div><h1>{{ stage?.title ?? 'Checklist' }}</h1></div>
    </header>

    <CkAlert v-if="checklistQuery.isError.value" tone="error">Não foi possível carregar o checklist desta etapa.</CkAlert>
    <p v-else-if="checklistQuery.isPending.value" class="status-message">Carregando…</p>
    <template v-else-if="stage">
      <div class="stage-layout">
        <aside class="rail" aria-label="Resumo e navegação da etapa">
          <section class="progress-card">
            <div class="progress-copy"><strong>{{ done }}/{{ items.length }} itens avaliados</strong><span class="ck-code">{{ Math.round(progress) }}%</span></div>
            <CkProgress :value="progress" label="Progresso da etapa" />
          </section>

          <div v-if="!online || pending.length" class="offline" role="status" aria-live="polite">
            <CloudOff :size="18" aria-hidden="true" />
            <span>{{ online ? `${pending.length} alteração(ões) pendente(s)` : 'Modo offline — alterações salvas neste dispositivo' }}</span>
            <CkButton v-if="online" variant="ghost" :disabled="syncing" @click="sync">{{ syncing ? 'Sincronizando…' : 'Sincronizar' }}</CkButton>
          </div>

          <nav class="stage-navigation" aria-label="Navegação entre etapas">
            <CkButton v-if="previousStage" variant="secondary" class="stage-nav-button" :aria-label="`Etapa anterior: ${previousStage.title}`" @click="navigateToStage(previousStage.id)">Anterior</CkButton>
            <span class="stage-count ck-code">{{ stageIndex + 1 }} / {{ sortedStages.length }}</span>
            <CkButton v-if="nextStage" variant="secondary" class="stage-nav-button" :aria-label="`Próxima etapa: ${nextStage.title}`" @click="navigateToStage(nextStage.id)">Próxima</CkButton>
          </nav>
        </aside>

        <main class="panel">
          <CkAlert v-if="uploadError" tone="error">Não foi possível enviar a foto. Tente novamente.</CkAlert>
          <input ref="fileInput" type="file" accept="image/*" capture="environment" class="sr-only" @change="onFileSelected">

          <section v-if="isArrival" class="cover-card">
            <div class="cover-preview" :class="{ empty: !coverUrl }">
              <img v-if="coverUrl" :src="coverUrl" alt="Foto do veículo">
              <ImagePlus v-else :size="26" aria-hidden="true" />
            </div>
            <div class="cover-body">
              <strong>Foto do veículo</strong>
              <p>Foto de perfil exibida na OS e no Portal do Cliente.</p>
              <CkButton variant="secondary" class="cover-btn" :disabled="coverUploading" @click="triggerCoverUpload">{{ coverUploading ? 'Enviando…' : (coverUrl ? 'Trocar foto' : 'Adicionar foto') }}</CkButton>
            </div>
          </section>
          <CkAlert v-if="coverUploadError" tone="error">Não foi possível enviar a foto do veículo. Tente novamente.</CkAlert>
          <input ref="coverInput" type="file" accept="image/*" capture="environment" class="sr-only" @change="onCoverSelected">

          <div class="checklist-grid">
            <section v-for="(groupItems, group) in groups" :key="group" class="group">
              <h2>{{ group }}</h2>
              <article v-for="item in groupItems" :key="item.id" class="item" :class="`item--${item.status}`">
                <div class="item-title"><strong>{{ item.title }}</strong><CkStatus :status="item.status" compact /></div>
                <textarea v-model="item.note" :aria-label="`Observação para ${item.title}`" placeholder="Adicionar observação"></textarea>
                <div v-if="photosByItem[item.id]?.length" class="item-photos">
                  <button
                    v-for="asset in photosByItem[item.id]"
                    :key="asset.id"
                    type="button"
                    class="item-photo"
                    :aria-label="`Ver foto ${asset.fileName}`"
                    @click="openPhotoId = asset.id"
                  ><img v-if="photoUrls[asset.id]" :src="photoUrls[asset.id]" :alt="asset.fileName"></button>
                </div>
                <div class="item-actions">
                  <CkSegmented :model-value="item.status" @update:model-value="(status) => setStatus(item, status)" />
                  <CkButton variant="secondary" class="photo-btn" :aria-label="`Registrar foto para ${item.title}`" :disabled="uploadingItemId === item.id" @click="triggerPhotoUpload(item.id)"><Camera :size="18" aria-hidden="true" /></CkButton>
                </div>
              </article>
            </section>
          </div>

          <StageRepairs :work-order-id="props.id" :stage-id="stage.id" />

          <CkAlert v-if="completeError" tone="error">Não foi possível concluir a etapa. Há reparos aguardando aprovação do cliente ou uma falha temporária.</CkAlert>
          <CkButton class="complete" :disabled="!canComplete" :aria-busy="completing" @click="completeStage">{{ completing ? 'Concluindo…' : `Concluir etapa de ${stage.title}` }}</CkButton>
          <p v-if="!online" class="complete-hint">Conecte-se para concluir a etapa. Suas avaliações permanecem salvas neste dispositivo.</p>
          <p v-else-if="stagePending.length" class="complete-hint">Aguarde a sincronização das alterações antes de concluir.</p>
        </main>
      </div>
    </template>
    <p v-else class="status-message">Etapa não encontrada.</p>

    <CkLightbox
      v-if="openPhoto && photoUrls[openPhoto.id]"
      :src="photoUrls[openPhoto.id]!"
      :alt="openPhoto.fileName"
      removable
      :removing="removingId === openPhoto.id"
      @close="openPhotoId = null"
      @remove="removeOpenPhoto"
    />
  </div>
</template>

<style scoped>
.app{max-width:1280px;min-height:100vh;margin:auto;padding:calc(20px + env(safe-area-inset-top)) 18px 40px;background:var(--ck-cream-100)}
.page-header{display:flex;align-items:center;gap:12px;margin-bottom:16px}
.back{display:flex;align-items:center;justify-content:center;width:44px;min-width:44px;height:44px;padding:0;border:1px solid var(--ck-cream-300);border-radius:var(--radius-md);background:var(--ck-cream-50);color:var(--ck-maroon-700)}
.page-header h1{margin:2px 0 0;font-size:24px;line-height:1.1}
.status-message{padding:20px 0;color:var(--ck-text-muted);font-size:13px}
.stage-layout{display:grid;grid-template-columns:1fr;gap:20px}
.rail,.panel{min-width:0}
.progress-card{padding:16px;margin-bottom:14px;border:1px solid var(--ck-cream-300);border-radius:var(--radius-lg);background:var(--ck-cream-50);box-shadow:var(--shadow-sm)}
.progress-copy{display:flex;justify-content:space-between;gap:12px;margin-bottom:10px;font-size:13px}
.progress-copy .ck-code{color:var(--ck-orange-500)}
.offline{display:flex;align-items:center;gap:8px;padding:10px 12px;margin-bottom:14px;border-radius:var(--radius-md);background:var(--ck-info-bg);color:var(--ck-info-text);font-size:12px}
.offline span{flex:1}
.offline .ck-button{min-height:32px;padding:5px 8px;color:inherit;font-size:12px}
.stage-navigation{display:flex;align-items:center;justify-content:space-between;gap:8px;padding:12px;border:1px solid var(--ck-cream-300);border-radius:var(--radius-lg);background:var(--ck-cream-50)}
.stage-nav-button{min-width:88px;padding:9px 12px}
.stage-count{color:var(--ck-text-muted);font-size:12px}
.cover-card{display:flex;gap:14px;align-items:center;padding:14px;margin-bottom:14px;border:1px solid var(--ck-cream-300);border-radius:var(--radius-lg);background:var(--ck-cream-50);box-shadow:var(--shadow-sm)}
.cover-preview{display:flex;align-items:center;justify-content:center;width:84px;height:64px;flex-shrink:0;border-radius:var(--radius-md);overflow:hidden;background:linear-gradient(135deg,var(--ck-maroon-700),var(--ck-ink-800));color:var(--ck-cream-100)}
.cover-preview img{width:100%;height:100%;object-fit:cover}
.cover-body{flex:1;min-width:0}
.cover-body strong{display:block;font-size:14px}
.cover-body p{margin:2px 0 8px;font-size:12px;color:var(--ck-text-muted)}
.cover-btn{min-height:36px;padding:7px 14px}
.checklist-grid{display:grid;grid-template-columns:1fr;gap:8px}
.group h2{margin:20px 0 10px;font-size:12px;letter-spacing:var(--ls-widest);text-transform:uppercase;color:var(--ck-maroon-700)}
.item{padding:14px;margin-bottom:2px;border-left:4px solid var(--ck-cream-300);border-radius:var(--radius-lg);background:var(--ck-cream-50);box-shadow:var(--shadow-sm)}
.item--ok{border-color:var(--ck-ok-500)}
.item--attention{border-color:var(--ck-warn-500)}
.item--rejected{border-color:var(--ck-fail-500)}
.item-title{display:flex;justify-content:space-between;gap:10px;align-items:flex-start}
.item textarea{width:100%;min-height:52px;margin:10px 0;padding:9px;border:1px solid var(--ck-cream-300);border-radius:var(--radius-md);background:var(--ck-cream-50);color:var(--ck-text);font:inherit;resize:vertical}
.item-photos{display:flex;flex-wrap:wrap;gap:6px;margin-bottom:10px}
.item-photo{padding:0;border:1px solid var(--ck-cream-300);border-radius:var(--radius-sm);overflow:hidden;width:48px;height:48px;cursor:pointer;background:var(--ck-ink-800)}
.item-photo img{display:block;width:100%;height:100%;object-fit:cover}
.item-actions{display:flex;align-items:stretch;gap:6px}
.item-actions .ck-segmented{flex:1}
.photo-btn{display:flex;align-items:center;justify-content:center;min-height:44px;min-width:44px;padding:8px}
.complete{width:100%;margin-top:20px}
.complete-hint{margin:8px 0 0;color:var(--ck-text-muted);font-size:12px;line-height:1.4}
.sr-only{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0,0,0,0)}

@media (min-width:640px) and (max-width:1199px) {
  .app{padding:32px 32px 52px}
  .stage-layout{grid-template-columns:minmax(260px,320px) minmax(0,1fr);gap:24px;align-items:start}
  .rail{position:sticky;top:24px}
  .checklist-grid{grid-template-columns:repeat(2,minmax(0,1fr));gap:12px}
  .group h2{margin-top:0}
}

@media (min-width:1200px) {
  .app{padding:40px 48px 64px}
  .stage-layout{grid-template-columns:320px minmax(0,1fr);gap:40px;align-items:start}
  .rail{position:sticky;top:24px}
  .panel{max-width:860px}
  .checklist-grid{grid-template-columns:repeat(2,minmax(0,1fr));gap:16px}
  .group h2{margin-top:0}
}

@media (max-width:375px) {
  .app{padding-left:14px;padding-right:14px}
  .page-header h1{font-size:22px}
  .cover-card{align-items:flex-start}
  .cover-preview{width:72px;height:56px}
  .stage-navigation{padding:8px}
  .stage-nav-button{min-width:82px}
  .item-title{flex-direction:column}
  .item-actions{flex-direction:column}
  .item-actions .photo-btn{width:100%}
}
</style>
